/**
 * The "Volumes by chainage" add-on — `AddOnToggle` (gated on
 * `analysis.stationVolumes`) around a `hideEnable` `StationVolumesFields`,
 * shared by every run dialog that offers station volumes, so the visibility
 * rule, the "can't apply" reasons and the auto-select-on-tick live in one place.
 *
 * Visibility: shows when there is an alignment to bin against OR a switched-off
 * LandXML layer to load one from (the load-from-LandXML path lives inside
 * `StationVolumesFields`), and on every tier — a locked reader still learns the
 * analysis exists. Also while it is ON, so a re-run pre-fill can always be
 * switched off again.
 *
 * The host keeps the form: values in, patches out (`onChange` is the dialog's
 * `applyStation`, see `applyStationPatch`).
 */
import { useId, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import type { Alignment } from '@/core/alignment';
import { useUnloadedAlignmentLayers } from '@/hooks/useUnloadedAlignmentLayers';

import { stationEnablePatch, StationVolumesFields, type StationVolumesFieldsProps } from '@/components/StationVolumesFields';
import { AddOnToggle } from './AddOnToggle';

export interface StationVolumesAddOnProps extends Omit<StationVolumesFieldsProps, 'hideEnable'> {
  /** The alignment registry (`alignmentsAtom`) — what ticking auto-selects from. */
  alignments: ReadonlyMap<string, Alignment>;
  /** A host-specific reason the add-on cannot apply (e.g. Deviation's
   *  horizontal mode). Takes precedence over "needs an alignment". */
  extraDisabledReason?: ReactNode;
  /** Wraps the body, e.g. Deviation's `<fieldset disabled>` while horizontal. */
  wrapBody?: (body: ReactNode) => ReactNode;
}

export function StationVolumesAddOn({
  alignments,
  extraDisabledReason,
  wrapBody,
  values,
  onChange,
  ...fields
}: StationVolumesAddOnProps) {
  const { t } = useTranslation('project');
  const id = useId();
  const { candidates: unloadedAlignmentLayers } = useUnloadedAlignmentLayers();
  if (!(alignments.size > 0 || unloadedAlignmentLayers.length > 0 || values.stationVolumesEnabled)) return null;

  const body = <StationVolumesFields hideEnable values={values} onChange={onChange} {...fields} />;
  return (
    <AddOnToggle
      id={id}
      testId='sv-enable'
      label={t('analysisConfig.addOns.stationVolumes')}
      help={t('analysisConfig.addOns.stationVolumesHelp')}
      gate='analysis.stationVolumes'
      checked={values.stationVolumesEnabled}
      onCheckedChange={(c) => onChange(stationEnablePatch(c, values.alignmentKey, alignments))}
      // Data conditions, not the gate: the host's own first, otherwise there
      // may be nothing to bin against yet (the body still offers to load one).
      disabledReason={
        extraDisabledReason ||
        (alignments.size === 0 && !values.stationVolumesEnabled ? t('analysisConfig.addOns.needsAlignment') : undefined)
      }
      revealChildren='always'>
      {wrapBody ? wrapBody(body) : body}
    </AddOnToggle>
  );
}
